import { https } from "../http";
import store from "../store";

import { setLoading, setMedications } from "../store/reducers/medication";
import { decrypt, showError } from "../utils/index";

// GET
const getMedications = async (page, showLoading = true) => {
  try {
    if (showLoading) store.dispatch(setLoading(true));
    let response = await https().get(`/medication/?page=${page}`);

    if (response?.status === 200) {
      let decrypted = decrypt(response?.data?.data);
      store.dispatch(setMedications(decrypted));
    }
  } catch (error) {
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const scanMedicationLabel = async (file) => {
  const formData = new FormData();
  formData.append("image", file);
  const response = await https().post("/medication/scan-ocr", formData);
  return response?.data?.data;
};

const scanMedicationLabelBulk = async (files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append("image", file));
  const response = await https().post("/medication/scan-ocr", formData);
  return response?.data?.data; // array of { index, filename, success, data? | error? }
};

const OCR_MULTI_POLL_INTERVAL_MS = 2500;
const OCR_MULTI_POLL_TIMEOUT_MS = 5 * 60 * 1000;

// Resolves after `ms`, or rejects immediately (AbortError) if `signal` fires first.
const waitOrAbort = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });

// Sends a single document (image/PDF-page) that may contain multiple medication orders.
// The backend runs this as an async job — large multi-image docs used to time out (503)
// waiting on a single request, so we submit the upload, then poll for the result.
// Returns an array of extracted medication objects.
// `onProcessing` fires once the job is accepted (202), before polling begins.
const scanMedicationLabelMulti = async (file, { signal, onProcessing } = {}) => {
  const formData = new FormData();
  formData.append("image", file);
  const submitResponse = await https().post("/medication/scan-ocr-multi", formData, {
    signal,
  });
  const jobId = submitResponse?.data?.jobId;
  onProcessing?.();

  const startedAt = Date.now();
  while (true) {
    if (Date.now() - startedAt > OCR_MULTI_POLL_TIMEOUT_MS) {
      throw Object.assign(new Error("Still processing — check back later"), {
        code: "OCR_MULTI_POLL_TIMEOUT",
      });
    }
    await waitOrAbort(OCR_MULTI_POLL_INTERVAL_MS, signal);
    const pollResponse = await https().get(`/medication/scan-ocr-multi/${jobId}`, {
      signal,
    });
    if (pollResponse?.data?.status === "done") return pollResponse.data.data; // array of medication objects
    // status === "processing" → keep polling
  }
};

export { getMedications, scanMedicationLabel, scanMedicationLabelBulk, scanMedicationLabelMulti };
